<script setup>
import notification from "@/utils/plugins/NotificationService";
import TrackerProjectInfo from "@/components/shared/TrackerProjectInfo.vue";
import { onBeforeUnmount, onMounted, ref, computed, watch, nextTick } from "vue";
import ProjectsService from "@/api/services/projects.service";
import TrackerService from "@/api/services/tracker.service";
import { normalizedIncludes } from "@/utils/textSearch";


// variables parametros
const page = ref(1);
const itemsPerPage = ref(9999);
const archived = ref(false);

// variables Clientes y proyecto
const optionClients = ref([]);
const allClients = ref([]);
const searchProject = ref('');
const selectorSearchTimeout = ref(null);

// variable loading
const isLoaded = ref(false);

// emits and props
const emit = defineEmits(['getProject'])
const props = defineProps({
    project: Object,
    client: Object,
    dropdownOpen: {
        type: Boolean,
        default: false,
    },
    selectedProjects: {
        type: Array,
        default: () => [],
    },
    optimizedSelector: {
        type: Boolean,
        default: false,
    },

})

const currentProject = computed(() => {
    return props.project;
})

const selectedClient = ref(props.client || null);
const refSearchProject = ref(null);
const first = ref(true)

const focusInput = () => {
    nextTick(() => {
        setTimeout(() => {
            refSearchProject.value?.focus()
        }, 200) // más margen para producción
    })
}

// Mounted
onMounted(() => {
    getClients();
    refSearchProject.value?.focus()
})

onBeforeUnmount(() => {
    clearTimeout(selectorSearchTimeout.value);
})

watch(() => props.dropdownOpen, (newValue) => {
    if (newValue) {
        focusInput()
    }
})


// getter
const getClients = () => {
    isLoaded.value = false;
    if (props.optimizedSelector) {
        const params = {
            search: searchProject.value,
            limit: 500,
        };
        if (selectedClient.value?.id) {
            params.clientId = selectedClient.value.id;
        }
        TrackerService.getProjectSelector(params)
            .then((response) => {
                optionClients.value = response.data['hydra:member'];
                allClients.value = optionClients.value;
            }).catch((error) => {
                console.log(error);
                notification.addNotification({
                    message: "Error al cargar los proyectos",
                    type: "error",
                    duration: 6000
                });
            })
            .finally(() => {
                isLoaded.value = true;
                focusInput();
            });
        return;
    }

    let params = {
        page: page.value,
        itemsPerPage: itemsPerPage.value,
        archived: archived.value,
        'order[name]': 'ASC'
    }
    ProjectsService.getClients(params)
        .then((response) => {
            // filtrar para que solo aparezcan los clientes con mas de 1 proyecto
            optionClients.value = response.data['hydra:member'].filter(client => {
                return client?.projectsNotArchived?.length > 0;
            });

            allClients.value = optionClients.value;
            searchForClientAndProject();

        }).catch((error) => {
            console.log(error);
            notification.addNotification({
                message: "Error al cargar los proyectos",
                type: "error",
                duration: 6000
            });
        })
        .finally(() => {
            isLoaded.value = true;
        });
}

const searchForClientAndProjectDebounced = () => {
    if (!props.optimizedSelector) {
        searchForClientAndProject();
        return;
    }

    clearTimeout(selectorSearchTimeout.value);
    selectorSearchTimeout.value = setTimeout(searchForClientAndProject, 180);
}

// function for the search
const searchForClientAndProject = () => {
    if (props.optimizedSelector) {
        getClients();
        return;
    }

    if (!allClients.value.length) {
        optionClients.value = [];
        return;
    }

    if (searchProject.value == '' || !searchProject.value) {
        optionClients.value = allClients.value;
    }
    let filterClients = allClients.value;
    if (selectedClient.value) {
        const temporal = allClients.value.map(client => {
            // Aquí comprueba que el nombre del cliente es igual al input
            const clientNameMatch = normalizedIncludes(client.name, selectedClient.value.name);

            // Si el nombre del cliente es igual al input, te muestra todos sus proyectos
            if (clientNameMatch) {
                return { ...client, projectsNotArchived: client?.projectsNotArchived };
            }
            return null;
        }).filter(result => result !== null);

        filterClients = temporal;
    }
    const temp = filterClients.map(client => {
        // Aquí comprueba que el nombre del cliente es igual al input
        const clientNameMatch = normalizedIncludes(client.name, searchProject.value);

        // Aquí comprueba que el nombre del proyecto sea igual al input
        const childPojectMatch = client?.projectsNotArchived.filter(project =>
            normalizedIncludes(project.name, searchProject.value)
        )

        // Si el nombre del cliente es igual al input, te muestra todos sus proyectos
        if (clientNameMatch) {
            return { ...client, projectsNotArchived: client?.projectsNotArchived };
        }

        // Si el nombre del proyecto es igual al input, solo te muestra esos proyectos con ese nombre 
        if (childPojectMatch.length > 0) {
            return { ...client, projectsNotArchived: childPojectMatch }
        }
        return null;
    }).filter(result => result !== null);
    optionClients.value = temp;
    if(first.value){
        focusInput();
        first.value = !first.value;
    }
}

watch(() => props.client, (newClient) => {
    selectedClient.value = newClient || null;
    searchForClientAndProject();
}, { immediate: true })


// function project is selected
const selectedProject = (project) => {
    searchProject.value = '';
    searchForClientAndProject();
    emit('getProject', project)
}

// Funcion para darle estilo al proyecto que este seleccionado
const getSelectedProjectStyle = (project) => {
    if (!props.project) return Object.assign({});
    if (!props.selectedProjects || props.selectedProjects.length < 1) {
        if (currentProject.value.id === project.id) {
            return Object.assign(
                { fontWeight: 700 }
            )
        } else {
            return Object.assign({})
        }
    } else {
        if (props.selectedProjects.some(item => item['@id'] === project['@id'])) {
            return Object.assign(
                { fontWeight: 700 }
            )
        } else {
            return Object.assign({})
        }

    }
}

const getIsSelectedProject = (project) => {
    if (!props.project) return false;
    if (!props.selectedProjects || props.selectedProjects.length < 1) {
        if (currentProject.value.id === project.id) {
            return true
        } else {
            return false;
        }
    } else {
        if (props.selectedProjects.some(item => item['@id'] === project['@id'])) {
            return true;
        } else {
            return false;
        }
    }
}



</script>
<template>
    <div class="initialDiv">
        <el-input ref="refSearchProject" v-model="searchProject" placeholder="Busca un proyecto"
            @input="searchForClientAndProjectDebounced()" class="projectInput mb-2" />

        <el-scrollbar class="scroll">
            <div v-for="client in optionClients" :key="client['@id']" :label="client.name" :value="client">
                <span class="trackerClient">
                    {{ client.name }}
                </span>
                <div v-for="project in client.projectsNotArchived" :key="project['@id']" :label="project.name"
                    :value="project">
                    <div class="row">
                        <p :class="getIsSelectedProject(project) ? 'col-11' : 'col-12'">
                            <tracker-project-info :project="project" class="projectTracker pointer"
                                @click="selectedProject(project)" :style="getSelectedProjectStyle(project)" />
                        </p>
                        <p class="col-1 d-flex justify-content-center align-content-end pointer"
                            v-if="getIsSelectedProject(project)">
                            <el-icon @click="selectedProject(project)">
                                <Close />
                            </el-icon>
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="!isLoaded" v-loading="!isLoaded" class="mt-5"></div>
            <div v-if="optionClients.length < 1 && isLoaded">
                <span class="trackerClient" style="color: #000;">
                    No se han encontrado proyectos
                </span>
            </div>
        </el-scrollbar>
    </div>
</template>
<style lang="scss" scoped>
.initialDiv {
    height: 100%;
    width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
}

.scroll {
    height: 240px;
    width: 100%;
    margin-top: 10px;
}

.projectInput {
    width: 100%;
}

.projectTracker {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.trackerClient {
    color: #474CE6;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 3px;
}
</style>
